import React from "react";

/* Multi-segment phase ring — 4 phases with distinct warm colors,
   active segment glows, current day marker dot on track. */

const PHASES = [
  { key: "menstrual", color: "hsl(0 89% 70%)", label: "Menstrual" },        /* tulip */
  { key: "follicular", color: "hsl(67 56% 65%)", label: "Follicular" },    /* spring */
  { key: "ovulation", color: "hsl(170 56% 56%)", label: "Ovulation" },     /* aquamarine */
  { key: "luteal", color: "hsl(222 80% 76%)", label: "Luteal" },           /* cornflower */
];

export default function PhaseRing({
  progress = 0,            // 0-100 cycle progress
  day,
  phaseLabel,
  activePhase,             // 'menstrual' | 'follicular' | 'ovulation' | 'luteal'
  size = 220,
  stroke = 16,
  textColor = "hsl(25 28% 16%)",
}) {
  const radius = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * radius;

  // 4 segments — each 25% of circle, with a small gap between
  const segments = PHASES.map((p, i) => {
    const segLen = circ * 0.235; // 23.5% visible, 1.5% gap
    const dashOffset = -i * (circ * 0.25);
    const isActive = activePhase === p.key;
    return { ...p, segLen, dashOffset, isActive };
  });

  // Marker position based on overall progress (0-100)
  const angle = (progress / 100) * 2 * Math.PI - Math.PI / 2;
  const markerX = cx + radius * Math.cos(angle);
  const markerY = cy + radius * Math.sin(angle);

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="absolute inset-0">
        {/* Track */}
        <circle
          cx={cx} cy={cy} r={radius}
          stroke="hsl(36 22% 86%)"
          strokeWidth={stroke}
          fill="none"
        />
        {/* Colored segments */}
        {segments.map((s) => (
          <circle
            key={s.key}
            cx={cx} cy={cy} r={radius}
            stroke={s.color}
            strokeWidth={s.isActive ? stroke + 2 : stroke - 2}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${s.segLen} ${circ - s.segLen}`}
            strokeDashoffset={s.dashOffset}
            opacity={s.isActive ? 1 : 0.55}
            style={{
              transform: "rotate(-90deg)",
              transformOrigin: `${cx}px ${cy}px`,
              filter: s.isActive ? `drop-shadow(0 0 8px ${s.color}66)` : "none",
              transition: "opacity 400ms ease, stroke-width 400ms ease",
            }}
          />
        ))}
        {/* Current-day marker dot */}
        {progress > 0 && (
          <g style={{ transition: "all 600ms ease" }}>
            <circle cx={markerX} cy={markerY} r={stroke / 1.4} fill="hsl(36 38% 96%)" />
            <circle cx={markerX} cy={markerY} r={stroke / 2.4} fill="hsl(25 28% 16%)" />
          </g>
        )}
      </svg>
      <div className="relative flex flex-col items-center justify-center text-center px-3" style={{ color: textColor }}>
        <p className="text-[9px] uppercase tracking-wider font-extrabold opacity-60">Day</p>
        <p className="metric-xl text-[68px] mt-1" data-testid="phase-ring-day">{day ?? "\u2014"}</p>
        <p className="text-[11px] font-medium opacity-80 mt-0.5 capitalize" data-testid="phase-ring-label">{phaseLabel ?? "Log to begin"}</p>
      </div>
    </div>
  );
}
